import type { Adjunto, CompraTerreno, DetalleDireccion, ItemCompra, ItemPrevisto, Moneda, Terreno } from "./api";
import { sinTildes } from "./buscar";
import { validarArchivo } from "./adjuntos";
import { formatFecha, formatMoneda, hoyISO } from "./format";
import { aCentavos, deCentavos, montoAInput, parseMonto, parseTipoCambio, tipoCambioAInput } from "./gastos";

// ------------------------------------------------------------------ formulario de la ubicación

/** Campos de la dirección que se cargan a mano (o completa el autocompletado). */
export const CAMPOS_DIRECCION = ["calle", "altura", "entre_calle_1", "entre_calle_2", "localidad", "partido", "provincia", "codigo_postal"] as const;
export type CampoDireccion = (typeof CAMPOS_DIRECCION)[number];

/** Largos máximos de la API (columnas de `terrenos`). */
const LARGOS: Record<CampoDireccion | "zonificacion", number> = {
  calle: 120, altura: 20, entre_calle_1: 120, entre_calle_2: 120, localidad: 120, partido: 120, provincia: 80, codigo_postal: 12, zonificacion: 80,
};

/** Cambiar uno de estos a mano deja vieja la dirección formateada y la ubicación de Google: se descartan y el
 * backend arma la dirección de nuevo con calle, altura, localidad y provincia. */
const CAMPOS_QUE_MUEVEN_EL_PIN: readonly CampoDireccion[] = ["calle", "altura", "localidad", "partido", "provincia"];

export interface FormTerreno extends Record<CampoDireccion, string> {
  /** Ocultos: los trae el autocompletado (o ya estaban guardados). */
  direccion: string;
  latitud: string;
  longitud: string;
  google_place_id: string;
  superficie_m2: string;
  zonificacion: string;
}

export type ErroresTerreno = Partial<Record<keyof FormTerreno, string>>;

export interface PayloadTerreno extends Record<CampoDireccion, string | null> {
  /** Ausente cuando hay calle pero no dirección formateada: la arma el backend. */
  direccion?: string | null;
  latitud: string | null;
  longitud: string | null;
  google_place_id: string | null;
  superficie_m2: string | null;
  zonificacion: string | null;
}

/** "350.50" → "350,5"; "1000.00" → "1000" (mismo criterio que los montos). */
export function superficieAInput(superficie: string | null): string {
  return superficie ? montoAInput(superficie) : "";
}

export function formTerreno(t: Terreno): FormTerreno {
  const f = {} as FormTerreno;
  for (const c of CAMPOS_DIRECCION) f[c] = t[c] ?? "";
  return {
    ...f,
    direccion: t.direccion ?? "", latitud: t.latitud ?? "", longitud: t.longitud ?? "", google_place_id: t.google_place_id ?? "",
    superficie_m2: superficieAInput(t.superficie_m2), zonificacion: t.zonificacion ?? "",
  };
}

/** Cambia un campo del form. Si es parte de la dirección (calle, altura, localidad…), descarta la dirección formateada
 * y la ubicación de Google, que ya no corresponden. */
export function cambiarCampoTerreno(f: FormTerreno, campo: CampoDireccion | "superficie_m2" | "zonificacion", valor: string): FormTerreno {
  const nuevo = { ...f, [campo]: valor };
  if ((CAMPOS_QUE_MUEVEN_EL_PIN as readonly string[]).includes(campo) && valor !== f[campo]) {
    return { ...nuevo, direccion: "", latitud: "", longitud: "", google_place_id: "" };
  }
  return nuevo;
}

/** Completa el form con lo que devolvió el autocompletado. Entre calles, superficie y zonificación quedan como estaban. */
export function aplicarDetalleDireccion(f: FormTerreno, d: DetalleDireccion): FormTerreno {
  return {
    ...f,
    direccion: d.direccion ?? "", calle: d.calle ?? "", altura: d.altura ?? "", localidad: d.localidad ?? "", partido: d.partido ?? "",
    provincia: d.provincia ?? "", codigo_postal: d.codigo_postal ?? "", latitud: d.latitud ?? "", longitud: d.longitud ?? "",
    google_place_id: d.google_place_id ?? "",
  };
}

export function validarTerreno(f: FormTerreno): ErroresTerreno {
  const e: ErroresTerreno = {};
  for (const [campo, maximo] of Object.entries(LARGOS) as [CampoDireccion | "zonificacion", number][]) {
    if (f[campo].trim().length > maximo) e[campo] = `Hasta ${maximo} caracteres`;
  }
  if (f.superficie_m2.trim() && parseMonto(f.superficie_m2) === null) e.superficie_m2 = "Ingresá una superficie mayor a cero";
  return e;
}

export function aPayloadTerreno(f: FormTerreno): PayloadTerreno {
  const p = {} as PayloadTerreno;
  for (const c of CAMPOS_DIRECCION) p[c] = f[c].trim() || null;
  const direccion = f.direccion.trim() || null;
  return {
    // Con calle y sin dirección formateada (carga a mano), no se manda: la arma el backend.
    ...(direccion || !p.calle ? { direccion } : {}),
    ...p,
    latitud: f.latitud || null,
    longitud: f.longitud || null,
    google_place_id: f.google_place_id || null,
    superficie_m2: f.superficie_m2.trim() ? parseMonto(f.superficie_m2) : null,
    zonificacion: f.zonificacion.trim() || null,
  };
}

// ------------------------------------------------------------------ tarjeta de la ubicación

export function tieneUbicacion(t: Terreno): boolean {
  return Boolean(t.direccion || t.calle || t.localidad || t.superficie_m2 || t.zonificacion);
}

/** La línea grande de la tarjeta: "calle altura" si está estructurada; si no, la dirección como se cargó. */
export function tituloDireccion(t: Terreno): string | null {
  if (t.calle) return [t.calle, t.altura].filter(Boolean).join(" ");
  return t.direccion;
}

/** "entre Gaona y Pringles" (o "entre Gaona" si hay una sola). */
export function textoEntreCalles(t: Pick<Terreno, "entre_calle_1" | "entre_calle_2">): string | null {
  const calles = [t.entre_calle_1, t.entre_calle_2].filter((c): c is string => Boolean(c?.trim()));
  return calles.length ? `entre ${calles.join(" y ")}` : null;
}

/** "Haedo, Morón, Buenos Aires · CP B1706". Si el partido se llama igual que la localidad, no se repite. */
export function textoLocalidad(t: Pick<Terreno, "localidad" | "partido" | "provincia" | "codigo_postal">): string | null {
  const partes: string[] = [];
  for (const p of [t.localidad, t.partido, t.provincia]) {
    if (p?.trim() && !partes.some((x) => sinTildes(x) === sinTildes(p))) partes.push(p.trim());
  }
  const cp = t.codigo_postal?.trim() ? `CP ${t.codigo_postal.trim()}` : null;
  const texto = [partes.join(", "), cp].filter(Boolean).join(" · ");
  return texto || null;
}

/** La dirección para el mapa: la formateada o, si no hay, la armada con las partes. */
export function direccionParaMapa(t: Terreno): string | null {
  if (t.direccion) return t.direccion;
  const calle = tituloDireccion(t);
  const lugar = [t.localidad, t.provincia].filter(Boolean).join(", ");
  return [calle, lugar].filter(Boolean).join(", ") || null;
}

// ------------------------------------------------------------------ autocompletado

/** Desde cuántas letras se piden sugerencias (la API da 422 con menos). */
export const MINIMO_BUSQUEDA = 3;

/** Token de sesión del autocompletado: uno por apertura del diálogo, así Google cobra la sesión como una sola. */
export function nuevoTokenSesion(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

// ------------------------------------------------------------------ mapa (Google Maps sin clave de API)

export interface Coordenadas {
  latitud: string;
  longitud: string;
}

/** Las coordenadas si están las dos; si no, null (el mapa busca por la dirección escrita). */
export function coordenadasDe(t: Pick<Terreno, "latitud" | "longitud">): Coordenadas | null {
  return t.latitud && t.longitud ? { latitud: t.latitud, longitud: t.longitud } : null;
}

const consulta = (direccion: string, coordenadas?: Coordenadas | null) =>
  encodeURIComponent(coordenadas ? `${coordenadas.latitud},${coordenadas.longitud}` : direccion);

/** Link para abrir la dirección en Google Maps (app o web). Con coordenadas, apunta al lugar exacto. */
export function urlMapa(direccion: string, coordenadas?: Coordenadas | null): string {
  return `https://www.google.com/maps/search/?api=1&query=${consulta(direccion, coordenadas)}`;
}

/** Mapa embebido en un iframe, sin clave: el modo `output=embed` del buscador de Google Maps. */
export function urlMapaEmbebido(direccion: string, coordenadas?: Coordenadas | null, zoom?: number): string {
  return `https://www.google.com/maps?q=${consulta(direccion, coordenadas)}${zoom ? `&z=${zoom}` : ""}&output=embed`;
}

// ------------------------------------------------------------------ compra: precio acordado y fecha de escritura

export interface FormPrecio {
  precio: string;
  fecha_escritura: string;
}

export type ErroresPrecio = Partial<Record<keyof FormPrecio, string>>;

export interface PayloadPrecio {
  precio_acordado_usd: string;
  fecha_escritura: string | null;
}

export function formPrecio(t: Terreno): FormPrecio {
  const precio = t.compra?.precio_acordado_usd ?? null;
  return { precio: precio ? montoAInput(precio) : "", fecha_escritura: t.fecha_escritura ?? "" };
}

/** El precio acordado es lo único obligatorio: la fecha de escritura se sabe recién cuando se firma. */
export function validarPrecio(f: FormPrecio): ErroresPrecio {
  return parseMonto(f.precio) === null ? { precio: "Ingresá el precio acordado en dólares" } : {};
}

export function aPayloadPrecio(f: FormPrecio): PayloadPrecio {
  return { precio_acordado_usd: parseMonto(f.precio) ?? "0", fecha_escritura: f.fecha_escritura || null };
}

// ------------------------------------------------------------------ compra: pagos a cuenta y gastos

/** `gasto`: ya pagado (con la compra confirmada). `previsto`: un gasto de la compra que se paga al escriturar. */
export type TipoItemCompra = "pago" | "gasto" | "previsto";

export const TEXTOS_ITEM: Record<TipoItemCompra, { titulo: string; boton: string; ejemplo: string; archivo: string; descripcion: string }> = {
  pago: { titulo: "Agregar pago a cuenta", boton: "Agregar pago", ejemplo: "Ej.: Seña, Refuerzo de seña", archivo: "Scan del recibo", descripcion: "Sale de la Caja con esta fecha, en el rubro Terreno." },
  gasto: { titulo: "Agregar gasto de la compra", boton: "Agregar gasto", ejemplo: "Ej.: Inscripción, Sellos", archivo: "Comprobante", descripcion: "Sale de la Caja con esta fecha, en el rubro Terreno." },
  previsto: {
    titulo: "Agregar gasto de la compra", boton: "Agregar gasto", ejemplo: "Ej.: Comisión inmobiliaria, Escribanía, Sellos", archivo: "Presupuesto",
    descripcion: "Queda previsto: no mueve plata hasta que confirmes la compra. Ahí pasa a gasto pagado el día de la escritura.",
  },
};

/** "Lo pagó" en un pago a cuenta: el fideicomiso, o fiduciantes y grupos de su bolsillo (se carga el aporte de cada
 * uno). Pedido de Mati, 2026-10-19: puede ser más de uno, cada uno con su parte; lo que no cubren lo pone el fideicomiso. */
export const PAGO_FIDEICOMISO = "fideicomiso";

/** Quién puso una parte: "f:<id>" (un fiduciante) o "g:<id>" (un grupo); "" = sin elegir. `monto` en texto; con una
 * sola línea, vacío = todo el pago. */
export interface LineaPuso {
  quien: string;
  monto: string;
}

export const quienFiduciante = (id: number) => `f:${id}`;
export const quienGrupo = (id: number) => `g:${id}`;

/** Opciones de quién puso: los fiduciantes y después los grupos ("Empresa X (grupo)"). */
export function opcionesQuienPuso(fiduciantes: { id: number; nombre: string }[], grupos: { id: number; nombre: string }[]) {
  return [
    ...fiduciantes.map((f) => ({ valor: quienFiduciante(f.id), etiqueta: f.nombre })),
    ...grupos.map((g) => ({ valor: quienGrupo(g.id), etiqueta: `${g.nombre} (grupo)` })),
  ];
}

export interface FormItem {
  /** Sólo en un pago a cuenta: vacío = lo pagó el fideicomiso. */
  pusieron: LineaPuso[];
  fecha: string;
  concepto: string;
  monto: string;
  moneda: Moneda;
  tipo_cambio: string;
  lleva_fee: boolean;
  archivo: File | null;
  /** De qué caja sale (pago o gasto; un previsto no mueve plata): id como texto, "" = la principal (no se manda). */
  caja_id: string;
  /** O repartido entre varias cajas (Mati, 2026-10-24); vacío = sale todo de `caja_id`. */
  partesCaja: ParteCaja[];
}

/** Cuánto salió de una caja: id y monto como texto. */
export interface ParteCaja {
  caja_id: string;
  monto: string;
}

export type ErroresItem = Partial<Record<"fecha" | "concepto" | "monto" | "tipo_cambio" | "archivo" | "pusieron" | "cajas", string>>;

export interface PayloadItem {
  fecha: string;
  concepto: string;
  monto: string;
  moneda: Moneda;
  tipo_cambio: string | null;
  lleva_fee?: boolean; // sólo gastos: un pago a cuenta nunca lleva honorarios
  aportantes?: { fiduciante_id?: number; grupo_id?: number; monto: string }[];
  caja_id?: number;
  cajas?: { caja_id: number; monto: string }[];
}

export type PayloadPrevisto = Omit<PayloadItem, "fecha" | "aportantes" | "lleva_fee" | "caja_id" | "cajas"> & { lleva_fee: boolean };

/** Los pagos al vendedor suelen ser en dólares; los gastos (escribanía, sellos), en pesos. */
export function formItem(tipo: TipoItemCompra, hoy = hoyISO(), tcSugerido?: string | null, cajaDefecto = ""): FormItem {
  return {
    pusieron: [], fecha: hoy, concepto: "", monto: "", moneda: tipo === "pago" ? "USD" : "ARS",
    tipo_cambio: tipoCambioAInput(tcSugerido), lleva_fee: true, archivo: null, caja_id: cajaDefecto, partesCaja: [],
  };
}

/** Para editar un previsto: sus datos (la fecha no se usa: al confirmar toma la de escritura). */
export function formPrevisto(p: ItemPrevisto, hoy = hoyISO()): FormItem {
  return {
    pusieron: [], fecha: hoy, concepto: p.concepto, monto: montoAInput(p.monto), moneda: p.moneda,
    tipo_cambio: tipoCambioAInput(p.tipo_cambio), lleva_fee: p.lleva_fee, archivo: null, caja_id: "", partesCaja: [],
  };
}

export function validarItem(f: FormItem, tipo: TipoItemCompra = "pago"): ErroresItem {
  const e: ErroresItem = {};
  if (tipo !== "previsto" && !f.fecha) e.fecha = "Ingresá la fecha";
  if (!f.concepto.trim()) e.concepto = "Este dato es obligatorio";
  else if (f.concepto.trim().length > 200) e.concepto = "Hasta 200 caracteres";
  if (parseMonto(f.monto) === null) e.monto = "Ingresá un monto mayor a cero";
  if (f.moneda === "ARS" && parseTipoCambio(f.tipo_cambio) === null) e.tipo_cambio = "Ingresá el tipo de cambio";
  const archivoInvalido = f.archivo ? validarArchivo(f.archivo) : null;
  if (archivoInvalido) e.archivo = archivoInvalido;
  const pusieron = tipo === "pago" ? errorPusieron(f) : null;
  if (pusieron) e.pusieron = pusieron;
  const cajas = tipo !== "previsto" ? errorPartesCaja(f) : null;
  if (cajas) e.cajas = cajas;
  return e;
}

function errorPartesCaja(f: FormItem): string | null {
  if (f.partesCaja.length === 0) return null;
  if (f.partesCaja.some((p) => !p.caja_id)) return "Elegí la caja de cada parte";
  if (new Set(f.partesCaja.map((p) => p.caja_id)).size < f.partesCaja.length) return "Hay una caja dos veces";
  const montos = f.partesCaja.map((p) => parseMonto(p.monto));
  if (!montos.every((m): m is string => m !== null)) return "Poné cuánto salió de cada caja";
  const total = parseMonto(f.monto);
  if (total === null) return null;
  const diferencia = aCentavos(total) - montos.reduce((a, m) => a + aCentavos(m), 0n);
  if (diferencia > 0n) return `Falta repartir ${formatMoneda(deCentavos(diferencia), f.moneda)}`;
  if (diferencia < 0n) return `Sobran ${formatMoneda(deCentavos(-diferencia), f.moneda)}: las cajas suman más que el pago`;
  return null;
}

/** Centavos que puso cada línea (con una sola y sin monto, todo el pago); null si falta alguno. */
function centavosPusieron(f: FormItem): bigint[] | null {
  const total = parseMonto(f.monto);
  const montos = f.pusieron.map((l) => parseMonto(l.monto) ?? (f.pusieron.length === 1 && !l.monto.trim() ? total : null));
  return montos.every((m): m is string => m !== null) ? montos.map(aCentavos) : null;
}

function errorPusieron(f: FormItem): string | null {
  if (f.pusieron.length === 0) return null;
  if (f.pusieron.some((l) => !l.quien)) return "Elegí quién puso cada parte";
  if (new Set(f.pusieron.map((l) => l.quien)).size < f.pusieron.length) return "Hay alguien dos veces";
  const total = parseMonto(f.monto);
  const partes = centavosPusieron(f);
  if (partes === null) return "Poné cuánto puso cada uno";
  const suma = partes.reduce((a, b) => a + b, 0n);
  if (total !== null && suma > aCentavos(total)) {
    return `Entre todos ponen ${formatMoneda(deCentavos(suma), f.moneda)}: más que el pago (${formatMoneda(total, f.moneda)})`;
  }
  return null;
}

/** Lo que pone el fideicomiso cuando los que pusieron no cubren todo el pago (texto con moneda); null si cubren todo,
 * si no hay nadie o si falta algún monto. */
export function restoDelFideicomiso(f: FormItem): string | null {
  const total = parseMonto(f.monto);
  const partes = f.pusieron.length > 0 ? centavosPusieron(f) : null;
  if (total === null || partes === null) return null;
  const resto = aCentavos(total) - partes.reduce((a, b) => a + b, 0n);
  return resto > 0n ? formatMoneda(deCentavos(resto), f.moneda) : null;
}

export function aPayloadPrevisto(f: FormItem): PayloadPrevisto {
  return {
    concepto: f.concepto.trim(), monto: parseMonto(f.monto) ?? "0", moneda: f.moneda,
    tipo_cambio: f.moneda === "ARS" ? parseTipoCambio(f.tipo_cambio) : null, lleva_fee: f.lleva_fee,
  };
}

export function aPayloadItem(f: FormItem, tipo: TipoItemCompra = "pago"): PayloadItem {
  const base = { fecha: f.fecha, ...aPayloadPrevisto(f) };
  if (tipo === "previsto") return base;
  const caja = f.partesCaja.length > 0
    ? { cajas: f.partesCaja.map((p) => ({ caja_id: Number(p.caja_id), monto: parseMonto(p.monto) ?? "0" })) }
    : f.caja_id ? { caja_id: Number(f.caja_id) } : {};
  const conCaja = { ...base, ...caja };
  if (tipo === "gasto") return conCaja;
  // Un pago a cuenta no lleva honorarios: si van, van en el gasto (sería cobrarlos dos veces).
  const { lleva_fee: _sinFee, ...pago } = conCaja;
  const total = parseMonto(f.monto) ?? "0";
  const aportantes = f.pusieron.map((l) => {
    const [tipoQuien, id] = l.quien.split(":");
    const monto = parseMonto(l.monto) ?? total;
    return tipoQuien === "g" ? { grupo_id: Number(id), monto } : { fiduciante_id: Number(id), monto };
  });
  return { ...pago, aportantes };
}

/** Concepto de un pago o gasto en las listas: "Seña · pagó Mati", "Escribanía · Escribanía López". */
export function conceptoItem(i: ItemCompra): string {
  if (i.pagado_por) return `${i.concepto} · pagó ${i.pagado_por}`;
  return i.proveedor ? `${i.concepto} · ${i.proveedor.nombre}` : i.concepto;
}

/** El aviso del diálogo de confirmar cuando hay previstos. */
export function textoPrevistosAlConfirmar(compra: CompraTerreno, fechaEscritura: string | null): string | null {
  const n = compra.previstos.length;
  if (n === 0) return null;
  const cuando = fechaEscritura ? `el ${formatFecha(fechaEscritura)}` : "el día de la escritura";
  const cuales = n === 1 ? "El gasto previsto" : `Los ${n} gastos previstos`;
  const pasan = n === 1 ? "pasa a gasto pagado" : "pasan a gastos pagados";
  const salen = n === 1 ? "sale" : "salen";
  return `${cuales} (${formatMoneda(compra.previstos_usd, "USD")}) ${pasan} ${cuando}, y ${salen} de la Caja. Revisá los montos antes de confirmar.`;
}

export function textoHonorarios(item: { lleva_fee: boolean; fee_monto_usd: string | null }): string {
  if (!item.lleva_fee || item.fee_monto_usd === null) return "Sin honorarios";
  return `Honorarios ${formatMoneda(item.fee_monto_usd, "USD")}`;
}

// ------------------------------------------------------------------ compra: confirmación

export interface RequisitoCompra {
  texto: string;
  cumplido: boolean;
}

/** El faltante del scan se resuelve dentro del diálogo de confirmar (se elige o se sube ahí). */
export const esFaltaDeScan = (texto: string) => /scan/i.test(texto);

/** Lo que pide la confirmación: los cumplidos (✓) que se pueden saber con la ficha y los faltantes (○) tal como los
 * manda el backend en `faltan_para_confirmar`. */
export function requisitosCompra(t: Terreno): RequisitoCompra[] {
  const compra = t.compra;
  if (!compra) return [];
  const faltan = compra.faltan_para_confirmar;
  const cumplidos: RequisitoCompra[] = [];
  if (t.fecha_escritura && !faltan.some((f) => /fecha/i.test(f))) {
    cumplidos.push({ texto: `Fecha de escritura: ${formatFecha(t.fecha_escritura)}`, cumplido: true });
  }
  if (compra.precio_acordado_usd !== null && !faltan.some((f) => /saldo|pagar/i.test(f))) {
    cumplidos.push({ texto: "Saldo de la escritura pagado", cumplido: true });
  }
  return [...cumplidos, ...faltan.map((texto) => ({ texto, cumplido: false }))];
}

/** "Confirmar compra" se habilita sin faltantes (salvo el scan, que se elige en el mismo diálogo). */
export function puedeConfirmarCompra(t: Terreno): boolean {
  const compra = t.compra;
  if (!compra || compra.confirmada || compra.precio_acordado_usd === null) return false;
  return compra.faltan_para_confirmar.every(esFaltaDeScan);
}

/** Preelige el scan: un adjunto que se llame "escritura…" o, si hay uno solo, ése. */
export function adjuntoEscrituraSugerido(adjuntos: Adjunto[]): number | null {
  const porNombre = adjuntos.find((a) => sinTildes(a.nombre).includes("escritura"));
  if (porNombre) return porNombre.id;
  return adjuntos.length === 1 ? (adjuntos[0]?.id ?? null) : null;
}

export interface FormConfirmar {
  escritura_adjunto_id: number | null;
  confirmo: boolean;
}

export type ErroresConfirmar = Partial<Record<keyof FormConfirmar, string>>;

export function validarConfirmar(f: FormConfirmar): ErroresConfirmar {
  const e: ErroresConfirmar = {};
  if (f.escritura_adjunto_id === null) e.escritura_adjunto_id = "Elegí el scan de la escritura";
  if (!f.confirmo) e.confirmo = "Marcá que la compra se escrituró";
  return e;
}
